import React, { useEffect, useRef, useState } from 'react';
import { X, ChevronLeft } from 'lucide-react';

interface Citation {
  id: number;
  text: string;
  claim?: string;
  category?: string;
  context?: string;
  page?: number;
}

interface HTMLViewerProps {
  htmlUrl: string;
  title: string;
  citations: Citation[];
  onClose: () => void;
}

const HTMLViewerWithHighlights: React.FC<HTMLViewerProps> = ({
  htmlUrl,
  title,
  citations,
  onClose,
}) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<number | null>(null);

  const scrollToHighlight = (cid: number | string) => {
    const highlight = document.querySelector(
      `[data-citation-id="${cid}"]`
    ) as HTMLElement | null;
    if (highlight) {
      console.log('🎯 Scrolling to citation', cid);
      highlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlight.classList.add('active');
      setTimeout(() => highlight.classList.remove('active'), 1500);
    } else {
      console.warn('⚠️ Highlight not found for citation', cid);
    }
  };

  // Fetch HTML content
  useEffect(() => {
    const fetchHTML = async () => {
      try {
        console.log('📄 Fetching HTML from:', htmlUrl);
        const response = await fetch(htmlUrl);
        const html = await response.text();
        setHtmlContent(html);
        setLoading(false);
        console.log('✅ HTML loaded');
      } catch (error) {
        console.error('❌ Failed to load HTML:', error);
        setLoading(false);
      }
    };
    fetchHTML();
  }, [htmlUrl]);

  // Highlight citation snippets inside the HTML content
  useEffect(() => {
    if (!htmlContent || !contentRef.current || citations.length === 0) return;
    const root = contentRef.current;

    citations.forEach((citation, index) => {
      const cid = citation.id ?? index;
      const candidates = [
        citation.text,
        citation.claim,
        citation.context,
      ].filter((t): t is string => !!t && t.length > 5);

      let matched = false;
      for (const searchText of candidates) {
        if (matched) break;
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        let node: Node | null;
        while ((node = walker.nextNode())) {
          const text = node.textContent || '';
          const position = text.indexOf(searchText);
          if (position !== -1 && node.parentElement) {
            const range = document.createRange();
            range.setStart(node, position);
            range.setEnd(node, position + searchText.length);
            const span = document.createElement('span');
            span.className = 'citation-highlight';
            span.dataset.citationId = cid.toString();
            span.onclick = () => {
              setActiveId(cid);
              scrollToHighlight(cid);
            };
            range.surroundContents(span);
            matched = true;
            break;
          }
        }
      }

      if (!matched) {
        console.warn('⚠️ No match found for citation', cid);
      }
    });
  }, [htmlContent, citations]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-white w-full h-full flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
          <button
            onClick={onClose}
            className="flex items-center text-sm text-gray-700 hover:text-gray-900"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            <span>Back to Report</span>
          </button>
          <h2 className="text-base font-semibold text-gray-900">Source Evidence Viewer</h2>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
            aria-label="Close viewer"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden bg-gray-50">
          {/* Left: AI Analysis */}
          <div className="w-96 border-r border-gray-200 overflow-auto bg-white">
            <div className="p-4 space-y-4">
              <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-900">
                AI analysis uses the source evidence on the right. Click a citation to jump to its highlight.
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-800">AI Analysis</h3>
                  <span className="text-xs text-gray-600">{citations.length} citations</span>
                </div>

                {citations.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No citations available</p>
                ) : (
                  <div className="space-y-3">
                    {citations.map((citation, index) => (
                      <div
                    key={index}
                    className={`border rounded-lg p-3 shadow-sm transition-all cursor-pointer relative ${
                      activeId === (citation.id ?? index)
                        ? 'border-blue-500 ring-1 ring-blue-200 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    onClick={() => {
                      const cid = citation.id ?? index;
                      setActiveId(cid);
                      const highlight = document.querySelector(
                        `[data-citation-id="${cid}"]`
                      ) as HTMLElement | null;
                      console.log('🖱️ Citation card clicked', { cid, highlightFound: !!highlight });
                      if (highlight) {
                        scrollToHighlight(cid);
                      }
                    }}
                  >
                        <span className="absolute top-2 right-2 text-xs font-semibold text-gray-500">
                          #{index + 1}
                        </span>
                        <div className="flex items-center justify-between mb-2">
                          {citation.category && (
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                              {citation.category}
                            </span>
                          )}
                        </div>
                        {citation.claim && (
                          <p className="text-sm font-semibold text-gray-900 mb-1">
                            {citation.claim}
                          </p>
                        )}
                        <p className="text-sm text-gray-700 italic mb-2">
                          "{citation.text?.substring(0, 150)}
                          {citation.text && citation.text.length > 150 ? '...' : ''}"
                        </p>
                        {citation.page && (
                          <p className="text-xs text-gray-600">Page {citation.page}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: HTML content */}
          <div className="flex-1 overflow-auto bg-gray-50 p-6">
            {loading ? (
              <div className="flex items-center justify-center h-full text-gray-600">
                Loading document…
              </div>
            ) : (
              <div
                ref={contentRef}
                className="bg-white shadow p-6 rounded html-document-content"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HTMLViewerWithHighlights;
